import { reactive, readonly } from "vue";
import { getApp, getApps, initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  onAuthStateChanged,
  setPersistence,
  signInWithCustomToken,
  signOut,
  type Auth,
  type User,
} from "firebase/auth";
import config from "@/app/configs";
import { firebaseConfig } from "@/app/plugins/firebase";
import { getErrorMessage } from "@/shared/utils/errorMessage";
import { buildKioskApiUrl } from "@/kiosk/utils/kioskApiUrl.utils";
import type { KioskLoginResponse, KioskSessionState } from "@/kiosk/types/kiosk";

const KIOSK_APP_NAME = "kiosk-app";
const KIOSK_TOKEN_HASH_KEY = "kioskTokenHash";
const KIOSK_LAUNCHER_TOKEN_HASH_KEY = "kioskLauncherTokenHash";
const kioskApiBase = config?.apis.base || "";

const kioskApp = getApps().some((app) => app.name === KIOSK_APP_NAME)
  ? getApp(KIOSK_APP_NAME)
  : initializeApp(firebaseConfig, KIOSK_APP_NAME);

const kioskAuth = getAuth(kioskApp);
let emulatorConnected = false;

if (import.meta.env.DEV && import.meta.env.MODE === "development" && !emulatorConnected) {
  connectAuthEmulator(kioskAuth, import.meta.env.VITE_AUTH_URL);
  emulatorConnected = true;
}

const getStorageSafely = (type: "localStorage" | "sessionStorage"): Storage | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window[type];
  } catch {
    return null;
  }
};

const readStoredValue = (storageKey: string): string | null => {
  const storages = [getStorageSafely("localStorage"), getStorageSafely("sessionStorage")];

  for (const storage of storages) {
    const value = storage?.getItem(storageKey) ?? null;

    if (value) {
      return value;
    }
  }

  return null;
};

const readStoredTokenHash = (): string | null => readStoredValue(KIOSK_TOKEN_HASH_KEY);

const readStoredLauncherTokenHash = (): string | null =>
  readStoredValue(KIOSK_LAUNCHER_TOKEN_HASH_KEY);

const state = reactive<KioskSessionState>({
  isReady: false,
  isRevoked: false,
  currentUser: null,
  tokenHash: readStoredTokenHash(),
});

let resolveReady: (() => void) | null = null;
const readyPromise = new Promise<void>((resolve) => {
  resolveReady = resolve;
});

const persistValue = (storageKey: string, value: string): void => {
  getStorageSafely("localStorage")?.setItem(storageKey, value);
  getStorageSafely("sessionStorage")?.setItem(storageKey, value);
};

const clearStoredValue = (storageKey: string): void => {
  getStorageSafely("localStorage")?.removeItem(storageKey);
  getStorageSafely("sessionStorage")?.removeItem(storageKey);
};

const persistTokenHash = (tokenHash: string): void => {
  persistValue(KIOSK_TOKEN_HASH_KEY, tokenHash);
  state.tokenHash = tokenHash;
};

const clearStoredTokenHash = (): void => {
  clearStoredValue(KIOSK_TOKEN_HASH_KEY);
  state.tokenHash = null;
};

const persistLauncherTokenHash = (tokenHash: string): void => {
  persistValue(KIOSK_LAUNCHER_TOKEN_HASH_KEY, tokenHash);
};

const clearStoredLauncherTokenHash = (): void => {
  clearStoredValue(KIOSK_LAUNCHER_TOKEN_HASH_KEY);
};

onAuthStateChanged(kioskAuth, (user) => {
  state.currentUser = user;
  state.isReady = true;

  if (!user) {
    clearStoredTokenHash();
  } else {
    state.isRevoked = false;
  }

  resolveReady?.();
  resolveReady = null;
});

const setTokenHash = (tokenHash: string) => {
  state.isRevoked = false;
  persistTokenHash(tokenHash);
};

export const setKioskLauncherTokenHash = (tokenHash: string): void => {
  const normalizedTokenHash = tokenHash.trim();
  if (!normalizedTokenHash) {
    return;
  }

  persistLauncherTokenHash(normalizedTokenHash);
};

const getRequestBody = async (response: Response) => {
  const rawBody = await response.text();
  if (!rawBody) {
    return null;
  }

  try {
    return JSON.parse(rawBody);
  } catch {
    return rawBody;
  }
};

export const kioskSessionState = readonly(state);

export const getKioskAuth = (): Auth => kioskAuth;

export const waitForKioskAuthReady = async (): Promise<void> => {
  if (state.isReady) {
    return;
  }

  await readyPromise;
};

export const getKioskSessionTokenHash = (): string | null => state.tokenHash;

export const getKioskLauncherTokenHash = (): string | null => readStoredLauncherTokenHash();

export const clearKioskLauncherTokenHash = (): void => {
  clearStoredLauncherTokenHash();
};

export const isKioskSessionRevoked = (): boolean => state.isRevoked;

export const hasKioskSession = (tokenHash?: string): boolean => {
  if (!kioskAuth.currentUser) {
    return false;
  }

  if (!tokenHash) {
    return true;
  }

  return state.tokenHash === tokenHash;
};

export const clearKioskSession = async (options: { revoked?: boolean } = {}): Promise<void> => {
  state.isRevoked = options.revoked ?? false;
  clearStoredTokenHash();
  await signOut(kioskAuth);
};

export const kioskLogin = async ({
  tokenHash,
  pin,
}: {
  tokenHash: string;
  pin: string;
}): Promise<KioskLoginResponse> => {
  await setPersistence(kioskAuth, browserLocalPersistence);

  const response = await fetch(buildKioskApiUrl(kioskApiBase, `kioskPublic/${tokenHash}/login`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ pin }),
  });

  const data = await getRequestBody(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(data, "Kiosk login failed"));
  }

  const typedData = data as KioskLoginResponse;
  await signInWithCustomToken(kioskAuth, typedData.customToken);
  setTokenHash(tokenHash);
  setKioskLauncherTokenHash(tokenHash);

  return typedData;
};

export const getKioskIdToken = async (): Promise<string> => {
  if (state.isRevoked) {
    throw new Error("Kiosk session expired");
  }

  const user = kioskAuth.currentUser;
  if (!user) {
    throw new Error("No kiosk session");
  }

  return user.getIdToken();
};

export const getKioskCurrentUser = (): User | null => kioskAuth.currentUser;
